import React from 'react';
import { logError } from '../firebase/errorReporting';

/**
 * Catches render-phase crashes so a thrown error shows a recoverable screen
 * instead of React unmounting the tree and leaving a blank white page.
 *
 * Only catches errors thrown during render, in lifecycle methods and in
 * constructors below it. Event handlers, async callbacks and promise
 * rejections do NOT reach here — installGlobalErrorHandlers() covers those.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    logError(error, {
      component: this.props.name || 'ErrorBoundary',
      componentStack: info?.componentStack?.slice(0, 2000),
      fatal: true,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        className="d-flex align-items-center justify-content-center bg-body-tertiary"
        style={{ minHeight: '100vh', padding: '2rem' }}
      >
        <div className="text-center" style={{ maxWidth: 480 }}>
          <h1 className="h4 mb-3">Something went wrong</h1>
          <p className="text-body-secondary mb-4">
            The map hit an unexpected error. It has been reported automatically — reloading
            usually fixes it.
          </p>
          <button type="button" className="btn btn-primary" onClick={this.handleReload}>
            Reload
          </button>
          {process.env.NODE_ENV !== 'production' && (
            <pre className="text-start mt-4 small text-danger">{this.state.message}</pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
